import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Loader2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { LOGOS, TIPO_ORG_LABEL } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

const TIPO_LOGO = {
  fornitore: LOGOS.fornitore,
  cliente: LOGOS.cliente,
  cliente_finale: LOGOS.cliente_finale,
};

const TIPO_STYLE = {
  fornitore: "border-indigo-200 bg-indigo-50 text-indigo-700",
  cliente: "border-amber-200 bg-amber-50 text-amber-700",
  cliente_finale: "border-teal-200 bg-teal-50 text-teal-700",
};

const RUOLI_PER_TIPO = {
  fornitore: "1 profilo: aggiorna i propri task fino a In Test",
  cliente: "3 profili: PM (sola lettura), Dev (opera e consolida), Tester (OK/KO)",
  cliente_finale: "1 profilo: sola visibilità su milestone e avanzamenti, scarica tutto",
};

export default function Organizzazioni() {
  const [orgs, setOrgs] = useState(null);
  const [form, setForm] = useState({ nome: "", tipo: "cliente", referente: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/organizzazioni").then(({ data }) => setOrgs(data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const crea = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/organizzazioni", form);
      toast.success(`${form.nome} configurata`);
      setForm({ nome: "", tipo: "cliente", referente: "", note: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const elimina = async (o) => {
    try {
      await api.delete(`/organizzazioni/${o.id}`);
      toast.success(`${o.nome} eliminata`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-7" data-testid="organizzazioni-page">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">Configurazione</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">Clienti & Aree</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-500 md:text-base">
          Configura la catena di delivery: fornitore del cliente, cliente e cliente finale. I profili si
          collegano a queste organizzazioni nella pagina Profili.
        </p>
      </div>

      <form onSubmit={crea} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="create-org-form">
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">Nuova organizzazione</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                   placeholder="Es. Majorbit" className="mt-1.5" data-testid="org-nome-input" />
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="mt-1.5" data-testid="org-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {Object.entries(TIPO_ORG_LABEL).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Referente</Label>
            <Input value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })}
                   placeholder="Nome e cognome" className="mt-1.5" data-testid="org-referente-input" />
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                   placeholder="Ruolo nella catena" className="mt-1.5" data-testid="org-note-input" />
          </div>
        </div>
        <Button type="submit" disabled={busy} className="mt-4 bg-slate-900 hover:bg-slate-800" data-testid="create-org-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Aggiungi
        </Button>
      </form>

      {orgs === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {orgs.map((o) => (
            <div key={o.id} data-testid={`org-card-${o.nome}`}
                 className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start gap-3">
                <img src={TIPO_LOGO[o.tipo] || LOGOS.governance} alt={o.nome}
                     className="h-11 w-11 shrink-0 rounded-lg bg-slate-50 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-slate-900">{o.nome}</p>
                  <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 font-mono text-[10px] ${TIPO_STYLE[o.tipo]}`}>
                    {TIPO_ORG_LABEL[o.tipo]}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => elimina(o)}
                        className="text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                        data-testid={`delete-org-${o.nome}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {o.referente && <p className="mt-3 text-sm text-slate-600">Referente: {o.referente}</p>}
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{o.note}</p>
              <p className="mt-3 flex items-start gap-1.5 border-t border-slate-100 pt-3 font-mono text-[11px] text-slate-400">
                <Building2 className="mt-0.5 h-3 w-3 shrink-0" /> {RUOLI_PER_TIPO[o.tipo]}
              </p>
            </div>
          ))}
        </div>
      )}

      <PmBot pagina="dashboard" />
    </div>
  );
}
